import React, { useRef, useState } from 'react';
import { Button } from '@view/components';
import { cn } from '@utils';
import { IViewSettings } from '@data/models';
import { ConfigProvider } from '@data/providers';
import { ISceneData } from '@data/providers/SceneDataProvider';
import { useCompanyColorScheme } from 'src/hooks/useCompanyColorScheme';
import styles from './styles.scss';
import TeethLikeSemitransparentIcon from 'assets/images/teethLikeSemitransparent_icon.svg?react';

const CHECK_ICON = '/assets/images/check_icon.svg';

interface SemitransparencySwitcherProps {
  viewSettings: IViewSettings;
  setViewSetting: (setting: keyof IViewSettings, value: any) => void;
  companyColorScheme?: ReturnType<typeof useCompanyColorScheme>[0];
  configProvider: ConfigProvider;
  sceneData?: ISceneData;
}

const SEMITRANSPARENCY_OPTIONS = [
  { value: 'off', label: 'Semitransparency off' },
  { value: 'both', label: 'Both Jaws Semitransparent' },
  { value: 'upper', label: 'Upper Jaw Semitransparent' },
  { value: 'lower', label: 'Lower Jaw Semitransparent' },
];

type SemiValue = 'off' | 'both' | 'upper' | 'lower';

function getCurrentSemiValue(upper: boolean, lower: boolean): SemiValue {
  if (upper && lower) return 'both';
  if (upper) return 'upper';
  if (lower) return 'lower';
  return 'off';
}

const SemitransparencySwitcher = ({
  viewSettings,
  setViewSetting,
  companyColorScheme
}: SemitransparencySwitcherProps) => {
  const [panelOpen, setPanelOpen] = useState(false);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const currentValue = getCurrentSemiValue(
    viewSettings.teethLikeSemitransparent.upperJaw,
    viewSettings.teethLikeSemitransparent.lowerJaw
  );

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    setPanelOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setPanelOpen(false);
    }, 600);
  };


  const handleOptionClick = (value: SemiValue) => {
    switch (value) {
      case 'off':
        setViewSetting('teethLikeSemitransparent', {upperJaw: false, lowerJaw: false});
        break;
      case 'both':
        setViewSetting('teethLikeSemitransparent', {upperJaw: true, lowerJaw: true});
        break;
      case 'upper':
        setViewSetting('teethLikeSemitransparent', {upperJaw: true, lowerJaw: false});
        break;
      case 'lower':
        setViewSetting('teethLikeSemitransparent', {upperJaw: false, lowerJaw: true});
        break;
    }
  };

  const handleButtonClick = () => {
    const options = SEMITRANSPARENCY_OPTIONS.map((v) => v.value);
    const currentIndex = options.indexOf(currentValue);
    const nextIndex = (currentIndex + 1) % options.length;
    const nextOption = options[nextIndex];
    
    handleOptionClick(nextOption as SemiValue);
  };

  return (
    <div className="relative">
      <Button
        active={currentValue !== 'off' || panelOpen}
        className={cn(styles.viewSettingsBar_button, 'min-h-full')}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleButtonClick}
      >
        <TeethLikeSemitransparentIcon 
          width={24} 
          height={24}
          className={cn(
            companyColorScheme?.light && 'invert-[.75] drop-shadow-lg'
          )}
        />
      </Button>
      {panelOpen && (
        <div
          className="absolute z-50 left-0 mt-2 w-64 bg-white rounded shadow-lg border border-gray-200"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {SEMITRANSPARENCY_OPTIONS.map((option) => (
            <div
              key={option.value}
              className={cn(
                'px-3 py-1.5 flex items-center justify-between hover:bg-gray-200 cursor-pointer',
                'text-base text-deepSpace'
              )}
              onClick={() => handleOptionClick(option.value as SemiValue)}
            >
              <span>{option.label}</span>
              {currentValue === option.value && (
                <img src={CHECK_ICON} alt="Check icon" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SemitransparencySwitcher; 